import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { ThemeProvider } from "@mui/material";
import { useGetSignedAgreements } from "@src/appV2/Agreements/api/useGetSignedAgreements";
import { NoSignedAgreementFileAvailable } from "@src/appV2/Agreements/NoSignedAgreementFileAvailable";
import { AgreementKind } from "@src/appV2/Agreements/types";
import { getNewestSignedAgreementFromResponse } from "@src/appV2/Agreements/utils/getNewestSignedAgreementFromResponse";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { toFileNameSafeString } from "@src/appV2/lib/utils/toFileNameSafeString";
import { PdfViewerProvider } from "@src/appV2/PdfViewer/context";
import { PdfViewer } from "@src/appV2/PdfViewer/PdfViewer";
import { PdfViewerLoadingSkeleton } from "@src/appV2/PdfViewer/PdfViewerLoadingSkeleton";
import { ACCOUNTS_BASE_PATH } from "@src/appV2/redesign/Accounts/paths";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useRef } from "react";

import { useSendJobSearchAgreementEmail } from "./api/useSendJobSearchAgreementEmail";

export function JobSearchAgreementPage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const worker = useDefinedWorker();
  const { showSuccessToast } = useToast();

  const {
    data: signedAgreements,
    isLoading: isLoadingAgreements,
    isError: isErrorAgreements,
    isSuccess: isSuccessAgreements,
  } = useGetSignedAgreements({
    workerId: worker.userId,
    kind: [AgreementKind.JOB_SEARCH],
  });

  const { mutateAsync: sendEmail, isLoading } = useSendJobSearchAgreementEmail();

  const fileName = `${toFileNameSafeString(worker.name)}_job_search_agreement.pdf`;
  const newestSignedAgreement = isSuccessAgreements
    ? getNewestSignedAgreementFromResponse(signedAgreements)
    : undefined;
  const pdfUrl = newestSignedAgreement?.attributes.downloadUrl;
  const agreementVersion = isDefined(newestSignedAgreement)
    ? `V${newestSignedAgreement.attributes.version}`
    : undefined;

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          title="Job Search Agreement"
          headerVariant="tertiary"
          scrollContainerRef={scrollRef}
          fallbackGoBackPath={ACCOUNTS_BASE_PATH}
        />

        <PdfViewerProvider fileName={fileName} pdfUrl={pdfUrl}>
          <PageContent>
            {isLoadingAgreements && <PdfViewerLoadingSkeleton />}
            {isSuccessAgreements && !isDefined(pdfUrl) && <NoSignedAgreementFileAvailable />}
            {isErrorAgreements && <NoSignedAgreementFileAvailable />}
            {isSuccessAgreements && isDefined(pdfUrl) && <PdfViewer />}
          </PageContent>

          {isSuccessAgreements && isDefined(pdfUrl) && isDefined(agreementVersion) && (
            <PageFooterWrapper>
              <LoadingButton
                fullWidth
                isLoading={isLoading}
                variant="contained"
                onClick={async () => {
                  try {
                    await sendEmail({ agreementVersion });
                    showSuccessToast(`Agreement emailed to ${worker.email}`);
                  } catch {
                    // Error already handled by mutation
                  }
                }}
              >
                Email me my agreement
              </LoadingButton>
            </PageFooterWrapper>
          )}
        </PdfViewerProvider>
      </PageWrapper>
    </ThemeProvider>
  );
}
